import { useMemo } from 'react';
import { useTypedQuery } from 'apiTyped/helpers/useTypedQuery';
import type {
  KpiTile,
  ScrumItem,
  MarketRow,
  MixDataItem,
  BreakdownItem
} from 'v2/pages/RoiDashboard.types';

export interface RoiDashboardFilters {
  market?: string | null;
  channel?: string | null;
  brand?: string | null;
  period?: string | null;
  productType?: string | null;
}

export const useRoiDashboard = (filters: RoiDashboardFilters = {}) => {
  const { isLoading, data, refetch } = useTypedQuery(
    'get',
    '/v2/roi/dashboard',
    {
      params: {
        query: {
          market: filters.market ?? undefined,
          channel: filters.channel ?? undefined,
          brand: filters.brand ?? undefined,
          period: filters.period ?? undefined,
          product_type: filters.productType ?? undefined
        }
      }
    }
  );

  const mapped = useMemo(() => {
    if (!data) return null;

    const breakdown: BreakdownItem[] = data.breakdown;

    const mapKpi = (k: (typeof data.distribution_kpis)[number]): KpiTile => ({
      label: k.label,
      value: k.value,
      unit: k.unit ?? undefined,
      delta: k.delta,
      direction: k.direction as KpiTile['direction'],
      sparkPath: k.spark_path,
      featured: k.featured ?? undefined
    });

    const distributionKpis = data.distribution_kpis.map(mapKpi);
    const engagementKpis = data.engagement_kpis.map(mapKpi);
    const competitiveKpis = data.competitive_kpis.map(mapKpi);

    const scrumItems: ScrumItem[] = data.scrum_items.map((s) => ({
      id: s.id,
      title: s.title,
      desc: s.desc,
      when: s.when,
      priority: s.priority as ScrumItem['priority']
    }));

    const mixData: MixDataItem[] = data.mix_data.map((m) => ({
      label: m.label,
      value: m.value,
      unit: m.unit,
      barWidth: m.bar_width,
      note: m.note
    }));

    const marketTable: MarketRow[] = data.market_table.map((m) => ({
      flag: m.flag,
      name: m.name,
      nd: m.nd,
      posWon: m.pos_won,
      posLost: m.pos_lost,
      loyalty: m.loyalty,
      novelty: m.novelty,
      mktShare: m.mkt_share,
      score: m.score
    }));

    return {
      breakdown,
      distributionKpis,
      engagementKpis,
      competitiveKpis,
      scrumItems,
      mixData,
      marketTable
    };
  }, [data]);

  return {
    isLoading,
    breakdown: mapped?.breakdown ?? [],
    distributionKpis: mapped?.distributionKpis ?? [],
    engagementKpis: mapped?.engagementKpis ?? [],
    competitiveKpis: mapped?.competitiveKpis ?? [],
    scrumItems: mapped?.scrumItems ?? [],
    mixData: mapped?.mixData ?? [],
    marketTable: mapped?.marketTable ?? [],
    refetch
  };
};
